import { ExcelExportColumn, ExcelExportWorksheetConfig } from "../types";
import { serializeTaxMaintenancesForExport } from "../../constants/TaxMaintenance";
import { readLinkedValues, serializeUniqueCodes } from "./multiValueExport";

export const updateFieldValue = (value: unknown) =>
  typeof value === "string" && value.includes(" - ")
    ? value.split(" - ")[0]
    : (value ?? null);

export const isEmptyValue = (value: unknown) =>
  value === undefined || value === null || value === "";

const TRUTHY_STRINGS = new Set(["true", "yes", "1"]);
const FALSY_STRINGS = new Set(["false", "no", "0"]);

export const isBooleanColumnDefault = (defaultValue: unknown): boolean =>
  typeof defaultValue === "boolean" ||
  defaultValue === "True" ||
  defaultValue === "False" ||
  defaultValue === "Yes" ||
  defaultValue === "No";

export const isNumericColumnDefault = (
  defaultValue: unknown,
): defaultValue is number =>
  typeof defaultValue === "number" && !Number.isNaN(defaultValue);

/** Visual One integer columns that must never use True/False coercion. */
const INTEGER_EXPORT_HEADERS = new Set(["RequireQuantity"]);

/** Visual One integer columns expect numeric string values, not empty strings. */
export const coerceVisualOneInteger = (
  value: unknown,
  defaultValue = 0,
): string => {
  if (value === null || value === undefined) {
    return String(defaultValue);
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(Number.isInteger(value) ? value : Math.trunc(value));
  }

  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return String(defaultValue);
    }

    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
      return String(Number.isInteger(parsed) ? parsed : Math.trunc(parsed));
    }
  }

  return String(defaultValue);
};

/** Visual One boolean columns expect "True" / "False", not JS booleans or yes/no. */
export const coerceVisualOneBoolean = (
  value: unknown,
  defaultValue?: unknown,
): string => {
  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }

  if (typeof value === "number") {
    return value !== 0 ? "True" : "False";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "True" || trimmed === "False") {
      return trimmed;
    }

    const lower = trimmed.toLowerCase();
    if (TRUTHY_STRINGS.has(lower)) return "True";
    if (FALSY_STRINGS.has(lower)) return "False";
  }

  const fallback =
    typeof defaultValue === "boolean"
      ? defaultValue
      : defaultValue === "True" || defaultValue === "Yes"
        ? true
        : defaultValue === "False" || defaultValue === "No"
          ? false
          : true;

  return fallback ? "True" : "False";
};

const resolveExportValue = (
  column: ExcelExportColumn,
  row: Record<string, unknown>,
): unknown => {
  const rawValue = column.sourceField ? row[column.sourceField] : undefined;
  const baseValue = !isEmptyValue(rawValue)
    ? updateFieldValue(rawValue)
    : column.defaultValue !== undefined
      ? column.defaultValue
      : null;

  if (INTEGER_EXPORT_HEADERS.has(column.header)) {
    const fallback = isNumericColumnDefault(column.defaultValue)
      ? column.defaultValue
      : 0;
    return coerceVisualOneInteger(baseValue, fallback);
  }

  if (
    isBooleanColumnDefault(column.defaultValue) ||
    typeof baseValue === "boolean"
  ) {
    return coerceVisualOneBoolean(baseValue, column.defaultValue);
  }

  if (
    isNumericColumnDefault(column.defaultValue) ||
    typeof baseValue === "number"
  ) {
    const fallback = isNumericColumnDefault(column.defaultValue)
      ? column.defaultValue
      : 0;
    return coerceVisualOneInteger(baseValue, fallback);
  }

  if (column.header === "taxMaintenances") {
    return serializeTaxMaintenancesForExport(baseValue);
  }

  if (
    column.header === "PlayerTypes" ||
    column.header === "RateTypes" ||
    column.header === "Outlets"
  ) {
    return serializeUniqueCodes(readLinkedValues(rawValue ?? baseValue));
  }

  return baseValue;
};

export const normalizeSheetRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  if (Array.isArray(sheetData)) {
    return sheetData;
  }

  if (typeof sheetData === "object" && sheetData !== null) {
    return [sheetData as Record<string, unknown>];
  }

  return [];
};

/** Maps one onboarding row to import/API column keys (JSON-shaped object). */
export const mapRowToExportColumns = (
  row: Record<string, unknown>,
  columns: ExcelExportColumn[],
): Record<string, unknown> =>
  columns.reduce(
    (acc, column) => {
      acc[column.header] = resolveExportValue(column, row);
      return acc;
    },
    {} as Record<string, unknown>,
  );

export const mapSheetToExportRows = (
  sheetData: unknown,
  columns: ExcelExportColumn[],
): Record<string, unknown>[] =>
  normalizeSheetRows(sheetData).map((row) =>
    mapRowToExportColumns(row, columns),
  );

export const mapWorksheetToExportRows = (
  sheetData: unknown,
  worksheet: ExcelExportWorksheetConfig,
): Record<string, unknown>[] => {
  const rawRows = worksheet.mapRows
    ? worksheet.mapRows(sheetData)
    : normalizeSheetRows(sheetData);

  return rawRows.map((row) => mapRowToExportColumns(row, worksheet.columns));
};
